/*
  SICLIPS moving-text ticker — <moving-text>
  A horizontal word marquee. Reads the hidden .mvt-mq__source nodes from the enclosing .mvt,
  clones them into two pixel-identical tracks, and a single CSS keyframe drives the seamless
  loop. JS only (a) fills short phrase lists up to the viewport width so a wrap never exposes a
  gap, and (b) adds optional hover-slow + drag-scrub. Motion (direction/speed) is 100% CSS.
  RTL: the belt is `direction: ltr` and the scroll side is set purely by animation-direction,
  so JS reads the COMPUTED direction via _dirSign() — never this.dir (that collides with the
  built-in HTMLElement.dir DOM property). If this JS never runs the tracks stay empty and the
  strip stays veiled until the CSS mvt-strip-reveal safety net un-veils it (~2.5s) so the
  server-rendered label is never lost.
*/
const MVT_FILL_MAX_ROUNDS = 24;
const MVT_SLOW = 0.28;

class MovingText extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this._gen = 0;

    this.root = this.closest('.mvt') || this;

    // The section CSS keeps the parent .mvt__strip invisible (opacity 0) until [data-mvt-ready]
    // is set — the band bg + hairlines live on the strip, so it would otherwise flash as an
    // empty colored band pre-build. It MUST be called before EVERY early return AND at the end
    // of the normal path, or the strip (incl. the Liquid-rendered label) stays stuck invisible.
    const strip = this.closest('.mvt__strip');
    const ready = function () { if (strip) strip.setAttribute('data-mvt-ready', ''); };

    this.viewport = this.querySelector('[data-mvt-viewport]');
    this.wrap = this.querySelector('[data-mvt-wrap]');
    this.tracks = Array.prototype.slice.call(this.querySelectorAll('[data-mvt-track]'));
    if (!this.viewport || !this.wrap || this.tracks.length < 2) { ready(); return; }

    this.hoverMode = this.dataset.hover || 'none'; // slow | pause | none
    this.dragEnabled = this.dataset.drag === 'true';

    this._reducedMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._onReduced = this._resume.bind(this);
    if (this._reducedMedia.addEventListener) this._reducedMedia.addEventListener('change', this._onReduced);

    this._buildTracks();
    if (this.hidden) { ready(); return; } // zero phrases — strip shows just the authored label
    this._fill();
    this._setupFill();

    if (this.hoverMode === 'slow' && !this._reducedMedia.matches) this._bindHover();
    if (this.dragEnabled) this._bindDrag();
    ready(); // built + filled — reveal the strip
  }

  disconnectedCallback() {
    this._gen++;
    if (this._ro) this._ro.disconnect();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._reducedMedia && this._reducedMedia.removeEventListener) {
      this._reducedMedia.removeEventListener('change', this._onReduced);
    }
  }

  /* ---- build the two tracks from the hidden source nodes ---- */
  _buildTracks() {
    const sources = this.root.querySelectorAll('.mvt-mq__source');
    const items = [];
    for (let i = 0; i < sources.length; i++) {
      const it = sources[i].querySelector('.mvt-mq__item');
      if (it) items.push(it);
    }
    if (!items.length) { this.hidden = true; return; }
    for (let t = 0; t < this.tracks.length; t++) {
      const track = this.tracks[t];
      track.textContent = '';
      for (let j = 0; j < items.length; j++) {
        const c = items[j].cloneNode(true);
        if (t === 1) { c.setAttribute('aria-hidden', 'true'); if (c.tagName === 'A') c.tabIndex = -1; }
        track.appendChild(c);
      }
    }
    this._baseCount = items.length;
  }

  /* ---- clone whole rounds until the live track fills the viewport; mirror to both ---- */
  _fill() {
    if (!this._baseCount) return;
    const target = this.viewport.offsetWidth;
    const live = this.tracks[0];
    const dup = this.tracks[1];
    this._clearFillers(live);
    this._clearFillers(dup);
    const base = Array.prototype.slice.call(live.children, 0, this._baseCount);
    if (!base.length) return;
    let rounds = 0;
    while (live.offsetWidth < target && rounds < MVT_FILL_MAX_ROUNDS) {
      this._appendRound(live, base);
      this._appendRound(dup, base);
      rounds++;
    }
  }
  _clearFillers(track) {
    const f = track.querySelectorAll('[data-mvt-filler]');
    for (let i = 0; i < f.length; i++) f[i].remove();
  }
  _appendRound(track, base) {
    for (let i = 0; i < base.length; i++) {
      const c = base[i].cloneNode(true);
      c.setAttribute('data-mvt-filler', '');
      c.setAttribute('aria-hidden', 'true');
      if (c.tagName === 'A') c.tabIndex = -1;
      track.appendChild(c);
    }
  }

  /* ---- re-fill on resize / web-font swap ---- */
  _setupFill() {
    const self = this;
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(function () { self._fill(); });
      this._ro.observe(this);
    } else {
      this._onResize = function () { self._fill(); };
      window.addEventListener('resize', this._onResize);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self._fill(); });
    }
  }

  /* ---- grab the running CSS animation (may not exist on the first frame) ---- */
  _withAnimation(cb) {
    const self = this;
    const gen = this._gen;
    let tries = 0;
    (function poll() {
      if (gen !== self._gen) return;
      const anim = self.wrap.getAnimations ? self.wrap.getAnimations()[0] : null;
      if (anim) { cb(anim); return; }
      if (tries++ > 30) return;
      requestAnimationFrame(poll);
    })();
  }

  _dirSign() {
    if (!this.wrap) return 1;
    return getComputedStyle(this.wrap).animationDirection === 'reverse' ? -1 : 1;
  }

  /* ---- hover: smoothly ramp playbackRate between 1 and MVT_SLOW ---- */
  _bindHover() {
    const self = this;
    const enter = function () { if (!self._dragging) self._ramp(MVT_SLOW); };
    const leave = function () { if (!self._dragging) self._ramp(1); };
    this.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'touch') enter(); });
    this.addEventListener('pointerleave', function (e) { if (e.pointerType !== 'touch') leave(); });
    this.addEventListener('focusin', enter);
    this.addEventListener('focusout', leave);
  }
  _ramp(to) {
    const self = this;
    this._withAnimation(function (anim) {
      const from = anim.playbackRate;
      if (from === to) return;
      let start = null;
      const dur = 320;
      const gen = self._gen;
      function step(ts) {
        if (gen !== self._gen) return;
        if (start === null) start = ts;
        const p = Math.min(1, (ts - start) / dur);
        const e = p * p * (3 - 2 * p);
        anim.playbackRate = from + (to - from) * e;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  /* ---- drag to scrub the timeline ---- */
  _bindDrag() {
    const self = this;
    this.style.touchAction = 'pan-y';
    this.addEventListener('pointerdown', function (e) { self._onDown(e); });
    this.addEventListener('pointermove', function (e) { self._onMove(e); });
    this.addEventListener('pointerup', function (e) { self._onUp(e); });
    this.addEventListener('pointercancel', function (e) { self._onUp(e); });
    this.addEventListener('click', function (e) { self._onClickCapture(e); }, true);
  }
  _measure(cb) {
    const self = this;
    this._withAnimation(function (anim) {
      const timing = anim.effect.getComputedTiming();
      self._dur = timing.duration || 1;
      const gs = getComputedStyle(self.wrap);
      const gap = parseFloat(gs.columnGap || gs.gap || '0') || 0;
      const period = self.tracks[0].offsetWidth + gap; // one repeating unit = one track + one gap
      self._msPerPx = period > 0 ? self._dur / period : 0;
      cb(anim);
    });
  }
  _onDown(e) {
    if (e.button !== 0) return;
    const self = this;
    this._measure(function (anim) {
      self._anim = anim;
      self._sign = self._dirSign();
      self._startX = e.clientX;
      self._baseTime = anim.currentTime || 0;
      self._dragging = true;
      self._moved = 0;
      anim.pause();
      self.classList.add('mvt--dragging');
      try { self.setPointerCapture(e.pointerId); } catch (err) {}
    });
  }
  _onMove(e) {
    if (!this._dragging || !this._anim) return;
    const delta = e.clientX - this._startX;
    this._moved = Math.abs(delta);
    if (this._moved > 6) e.preventDefault();
    this._scrub(this._baseTime - delta * this._msPerPx * this._sign);
  }
  _onUp(e) {
    if (!this._dragging) return;
    this._dragging = false;
    this.classList.remove('mvt--dragging');
    try { this.releasePointerCapture(e.pointerId); } catch (err) {}
    if (this._moved > 6) this._armClick();
    this._resume();
    this._anim = null;
  }
  _scrub(t) {
    if (!this._anim) return;
    const dur = this._dur || 1;
    t = ((t % dur) + dur) % dur;
    this._anim.currentTime = t;
  }
  _armClick() {
    this._suppressClick = true;
    const self = this;
    setTimeout(function () { self._suppressClick = false; }, 400);
  }
  _onClickCapture(e) {
    if (this._suppressClick) { e.preventDefault(); e.stopPropagation(); this._suppressClick = false; }
  }

  /* ---- resume playback unless reduced-motion (still draggable) or paused-on-hover ---- */
  _resume() {
    const self = this;
    this._withAnimation(function (anim) {
      if (self._reducedMedia && self._reducedMedia.matches) { anim.pause(); return; }
      anim.play();
      anim.playbackRate = 1;
    });
  }
}

if (!customElements.get('moving-text')) {
  customElements.define('moving-text', MovingText);
}
